import { PrismicRichText } from '@prismicio/react';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import BgImageFive from '../assets/cta-variant-five.png';
import BgImageFour from '../assets/cta-variant-four.png';
import BgImageOne from '../assets/cta-variant-one.svg';
import BgImageThree from '../assets/cta-variant-three.png';
import BgImageTwo from '../assets/cta-variant-two.png';

import { Section as SectionComponent, Label, Description } from './BenefitsOverviewSlice';

import { fieldHasContent, transformProperties } from '@/src/domains/prismic/helpers';
import { desktopBreakpoint, fromDesktopBreakpoint } from '@/src/domains/shared/breakpoints';
import { SimpleFormModalButton } from '@/src/views/Referrals/SimpleForm';

export const Title = styled.div`
  ${({ theme }) => theme.typography.scale4}
  color: var(--colors-blank);
  font-family: var(--typography-fontBossa);
  ${fluidProp('font-size', '28px', '36px')}
  max-width: 90%;
  margin: auto;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const DescriptionWrapper = styled(Description)`
  && {
    max-width: 80%;
    margin-left: auto;
    margin-right: auto;
    color: ${({ theme }) => theme.colors.blank_75};
  }
`;

export const CTAButton = styled(Button)`
  && {
    background-color: var(--colors-blank);
    color: var(--primaryColor);
    text-transform: uppercase;
    ${({ theme }) => theme.typography.subHead};
    margin-top: 40px;
    max-height: 46px;
  }
`;

const CtaBgStyle = css`
  background-size: cover;

  ${fromDesktopBreakpoint(css`
    ${ContentContainer} {
      display: flex;
      text-align: left;
      justify-content: space-around;
      align-items: center;
    }

    ${Title}, ${DescriptionWrapper} {
      margin-left: 0;
      max-width: 464px;
    }
  `)}
`;

const ImageWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 0;
  display: none;
`;

export const Section = styled(SectionComponent)`
  && {
    ${fluidProp(['padding-top', 'padding-bottom'], '70px', '100px')}
    overflow: hidden;
    text-align: center;
    background-color: var(--primaryColor);

    ${ContentContainer} {
      z-index: 1;
      position: relative;
    }

    ${({ $variant }) => {
      switch ($variant) {
        case 'second':
          return css`
            background-image: url(${BgImageTwo.src});
            background-size: cover;
          `;
        case 'third':
          return css`
            background-color: var(--secondaryColor);
            background-image: url(${BgImageThree.src});
            background-size: cover;
          `;
        case 'fourth':
          return css`
            background-color: var(--additionalBgColor);
            background-image: url(${BgImageFour.src});
            ${CtaBgStyle}
          `;

        case 'fifth':
          return css`
            background-image: url(${BgImageFive.src});
            ${CtaBgStyle}
          `;

        default:
          return css`
            ${ImageWrapper} {
              display: block;

              ${desktopBreakpoint(css`
                display: none;
              `)}
            }
          `;
      }
    }}
  }
`;

export function CtaSlice({ slice }) {
  const { primary, context } = transformProperties(slice);
  const { label, title, description, ctaLink, ctaLabel, variant } = primary;

  const {
    formId,
    gatedFile,
    modalFormTitle,
    modalFormDescription,
    modalFormSubmitLabel,
    downloadButtonLabel,
    eventName,
  } = context;
  return (
    <Section $variant={variant}>
      <ContentContainer>
        <div>
          {fieldHasContent(label) && <Label>{label}</Label>}
          {fieldHasContent(title) && (
            <Title>
              <PrismicRichText field={title} />
            </Title>
          )}
          {fieldHasContent(description) && (
            <DescriptionWrapper>
              <PrismicRichText field={description} />
            </DescriptionWrapper>
          )}
        </div>
        {ctaLink && (
          <SimpleFormModalButton
            modalFormId={formId}
            modalFormSubmitLabel={modalFormSubmitLabel}
            modalFormDescription={modalFormDescription}
            modalFormTitle={modalFormTitle}
            gatedFile={gatedFile}
            gatedFileDownloadLabel={downloadButtonLabel}
            eventName={eventName}
            button={{ as: CTAButton }}
          >
            {ctaLabel}
          </SimpleFormModalButton>
        )}
      </ContentContainer>
      <ImageWrapper>
        <BgImageOne />
      </ImageWrapper>
    </Section>
  );
}
